/**
 * @typedef {import('../MapLibreMap.js').MLD} MLD
 */

/**
 * @typedef {Object} IMapPopupProps
 * @property {MLD.PopupOptions=} popupOptions
 * @property {MLD._LngLat=} lngLat
 * @property {CSSStyleDeclaration=} style
 * @property {boolean=} closeOnMapClick
 * @property {boolean=} closeOnEscape
 * @property {boolean=} preventDefaultScroll
 * @property {MLD._PointLike=} focusOffset
 * @property {((event:MLD.Event)=>void)=} onOpen
 * @property {((event:MLD.Event)=>void)=} onClose
 */

window.OSM = window.OSM ?? {};
OSM.MapPopup = class MapPopup {

    /** @type {OSM.MapLibreMap} */
    map;

    /** @type {IMapPopupProps} */
    props;

    /** @type {MLD.PositionAnchor} */
    anchor= "bottom";

    /** @type {MLD.Offset} */
    offset= new maplibregl.Point(0, 0);

    /** @type {HTMLElement} */
    popupContent;

    /** @type {MLD._Popup|null} */
    popup= null;

    /** @type {MLD.PopupOptions} */
    popupOptions;

    /** @type {MLD._Point|null} */
    focusOffset = null;

    padding = 10;

    /**
     *
     * @param {OSM.MapLibreMap} map
     * @param {IMapPopupProps} props
     */
    constructor(map, props) {
        this.map = map;
        this.props = props ?? {};
        this.initialize();
    }


    initialize(){
        this.bindFunctions();
        this.applyProps();
        this.constructPopup();
        if(this.map.showSelectedMarkerPopup){
            this.showPopup();
        }
    }

    bindFunctions()
    {
        this.handleOnOpen = this.handleOnOpen.bind(this);
        this.handleOnClose = this.handleOnClose.bind(this);
    }

    applyProps()
    {
        const props = this.props;
        this.popupOptions = {
            ...props.popupOptions,
            closeOnClick: props.popupOptions?.closeOnClick ?? false,
            maxWidth: props.popupOptions?.maxWidth ?? "unset",
            anchor: props.popupOptions?.anchor ?? this.anchor,
            offset: props.popupOptions?.offset ?? undefined,
        }
        this.anchor = this.popupOptions.anchor;
        this.offset = this.popupOptions.offset ?? new maplibregl.Point(0, 0);

    }

    constructPopup()
    {
        this.popup = new maplibregl.Popup(this.popupOptions);
        this.popupContent = document.createElement("div");
        this.popupContent.className = "map-marker-popup-content";
        this.popup.setDOMContent(this.popupContent)
        if(this.props.lngLat){
            this.popup.setLngLat(this.props.lngLat);
        }
        this.popup.on('open', this.handleOnOpen);
        this.popup.on('close', this.handleOnClose);
    }

    /**
     *
     * @param activate {boolean=}
     */
    showPopup(activate)
    {
        activate = !!activate;
        if(activate){
            this.map.showSelectedMarkerPopup = true;
        }
        const popupMapMarker = this.getActivePopupMarker()
        if(!popupMapMarker || !this.map.isMapReady){
            this.closePopup();
            return;
        }
        if(!this.props.lngLat){
            this.popup.setLngLat(popupMapMarker.getLngLat());
        }
        if(!this.popupOptions.offset){
            this.offset = popupMapMarker.icon.getPopUpOffset(this.directionAnchor())
            this.popup.setOffset(this.offset);
        }
        this.popupContent.innerHTML = typeof popupMapMarker.popupContent === 'function' ? popupMapMarker.popupContent(popupMapMarker) : popupMapMarker.popupContent;

        if(!this.popup.isOpen()){
            this.popup.addTo(this.map.map)
        }
    }

    /**
     @returns {void}
     */
    closePopup()
    {
        if(!this.popup?.isOpen()){
            return;
        }
        this.popup.remove();
    }

    /**
     *
     * @param event {MLD.Event}
     */
    handleOnOpen(event){

        OSM.removeFocus();
        this.applyMaxPopupSize()
        this.determineFocusOffset()
        if(this.props.onOpen){
            this.props.onOpen(event);
        }
        setTimeout(() => {
            if(this.props.preventDefaultScroll){
                window.scrollBy(0, 0);
            }
        }, 1)
    }

    /**
     *
     * @param event {MLD.Event}
     */
    handleOnClose(event){
        this.getActivePopupMarker()?.onPopupClose();
        this.map.showSelectedMarkerPopup = false;
        if(this.props.onClose){
            this.props.onClose(event);
        }
    }


    /**
     @returns {void}
     */
    applyMaxPopupSize()
    {
        const popupMapMarker = this.getActivePopupMarker()
        if(!popupMapMarker){
            return;
        }
        const mapOuterContainer = this.map.outerContainer;
        /** @type {[number, number]|null} */
        const iconSize = popupMapMarker.icon?.size ?? null;
        const iconSizeWidth = iconSize ? iconSize[0] : 0;
        const iconSizeHeight = iconSize ? iconSize[1] : 0;

        const verticalAnchor = this.verticalAnchor();
        const canSetWidth = (verticalAnchor !== "center") || !!popupMapMarker.focusOffset;
        const canSetHeight = !!popupMapMarker.focusOffset;

        let maxWidth = 0;
        let maxHeight = 0;
        if(canSetWidth){
            maxWidth = mapOuterContainer?.clientWidth ?? 0;
            const horizontalOffset = popupMapMarker.focusOffset?.x ?? 0
            maxWidth -= Math.abs(horizontalOffset * maxWidth);
            maxWidth -= iconSizeWidth;
            maxWidth = Math.floor(maxWidth)
        }

        if(canSetHeight){
            maxHeight =  mapOuterContainer?.clientHeight ?? 0;
            const verticalOffset = popupMapMarker.focusOffset?.y ?? 0
            maxHeight = ((verticalOffset + 1) / 2) * maxHeight;
            maxHeight -= iconSizeHeight;
            maxHeight = Math.floor(maxHeight)
        }

        if(maxWidth > 0){
            document.body.style.setProperty("--max-map-popover-width", maxWidth + "px");
        } else {
            document.body.style.removeProperty('--max-map-popover-width');
        }
        if(maxHeight > 0){
            document.body.style.setProperty("--max-map-popover-height", maxHeight + "px");
        } else {
            document.body.style.removeProperty('--max-map-popover-height');
        }


    }

    determineFocusOffset()
    {
        this.focusOffset = null;

        const fromPopUpOffset = this.props.focusOffset ?? null

        if(fromPopUpOffset){
            this.focusOffset = OSM.requirePoint(fromPopUpOffset);
            return;
        }

        const padding = this.padding;

        const popupMapMarker = this.getActivePopupMarker()
        const element = this.popup?.getElement() ?? null;
        const outerContainer = this.map.outerContainer;

        const horizontalAnchor = this.horizontalAnchor();
        const verticalAnchor = this.verticalAnchor();
        const popUpOffset = popupMapMarker?.icon?.getPopUpOffset(this.directionAnchor());

        if(!element || !popupMapMarker || !outerContainer || !popUpOffset){
            return;
        }

        const containerWidth =  outerContainer.clientWidth;
        const containerHeight =  outerContainer.clientHeight;

        const occupiedPopupWidth = element.offsetWidth;
        const occupiedPopupHeight = element.offsetHeight;

        const popupOffsetX = popUpOffset.x;
        const occupiedPopupWidthProp = occupiedPopupWidth / containerWidth;
        const paddingXProp = padding / containerWidth;
        const popupOffsetXProp = popupOffsetX / containerWidth;

        let xMinimum;
        let xMaximum;
        if(horizontalAnchor === "left"){
            xMinimum = -0.5 + paddingXProp;
            xMaximum = (-0.5 + occupiedPopupWidthProp + paddingXProp + popupOffsetXProp) * -1;
        } else if(horizontalAnchor === "center"){
            xMinimum = -0.5 + occupiedPopupWidthProp / 2 + paddingXProp - popupOffsetXProp;
            xMaximum = (-0.5 + occupiedPopupWidthProp / 2 + paddingXProp + popupOffsetXProp) * -1;
        } else {
            xMinimum = -0.5 + occupiedPopupWidthProp + paddingXProp - popupOffsetXProp;
            xMaximum = 0;
        }
        let xOffset = Math.min(0, xMaximum);
        xOffset = Math.max(xOffset, xMinimum);

        const popupOffsetY = popUpOffset.y;
        const occupiedPopupHeightProp = occupiedPopupHeight / containerHeight;
        const paddingYProp = padding / containerHeight;
        const popupOffsetYProp = popupOffsetY / containerHeight;

        let yMinimum;
        let yMaximum;
        if(verticalAnchor === "top"){
            yMinimum = -0.5 + paddingYProp;
            yMaximum = (-0.5 + occupiedPopupHeightProp + paddingYProp + popupOffsetYProp) * -1;
        } else if(verticalAnchor === "center"){
            yMinimum = -0.5 + occupiedPopupHeightProp / 2 + paddingYProp - popupOffsetYProp;
            yMaximum = (-0.5 + occupiedPopupHeightProp / 2 + paddingYProp + popupOffsetYProp) * -1;
        } else {
            yMinimum = -0.5 + occupiedPopupHeightProp + paddingYProp - popupOffsetYProp;
            yMaximum = 0;
        }
        let yOffset = Math.min(0, yMaximum);
        yOffset = Math.max(yOffset, yMinimum);

        this.focusOffset = new maplibregl.Point(xOffset, yOffset)

    }

    /**
     *
     * @param targetLngLat {MLD._LngLat}
     * @returns {boolean}
     */
    willPopupFit(targetLngLat)
    {

        const popupMapMarker = this.getActivePopupMarker()

        const element = this.popup?.getElement() ?? null;
        const outerContainer = this.map.outerContainer;
        if(!element || !popupMapMarker || !outerContainer){
            return false;
        }

        const locationManager = OSM.manager.getLocationManager();

        const bounds = this.map.map.getBounds();
        const distanceInMetresToBoundsWest = locationManager.getDistanceInMetresBetween(targetLngLat, new maplibregl.LngLat(bounds.getWest(), targetLngLat.lat));
        const distanceInMetresToBoundsEast = locationManager.getDistanceInMetresBetween(targetLngLat, new maplibregl.LngLat(bounds.getEast(), targetLngLat.lat));
        const distanceInMetresToBoundsNorth = locationManager.getDistanceInMetresBetween(targetLngLat, new maplibregl.LngLat(targetLngLat.lng, bounds.getNorth()));
        const distanceInMetresToBoundsSouth = locationManager.getDistanceInMetresBetween(targetLngLat, new maplibregl.LngLat(targetLngLat.lng, bounds.getSouth()));
        const boundsCenter = this.map.getCameraCenterForBounds(bounds);
        const boundsWidthInMetres = locationManager.getDistanceInMetresBetween(new maplibregl.LngLat(bounds.getWest(), boundsCenter.lat), new maplibregl.LngLat(bounds.getEast(), boundsCenter.lat));
        const boundsHeightInMetres = locationManager.getDistanceInMetresBetween(new maplibregl.LngLat(boundsCenter.lng, bounds.getNorth()), new maplibregl.LngLat(boundsCenter.lng, bounds.getSouth()));

        const containerWidth =  outerContainer.clientWidth;
        const containerHeight =  outerContainer.clientHeight;

        let distanceInPxToLeft = (distanceInMetresToBoundsWest / boundsWidthInMetres) * containerWidth;
        let distanceInPxToRight = (distanceInMetresToBoundsEast / boundsWidthInMetres) * containerWidth;
        let distanceInPxToTop = (distanceInMetresToBoundsNorth / boundsHeightInMetres) * containerHeight;
        let distanceInPxToBottom = (distanceInMetresToBoundsSouth / boundsHeightInMetres) * containerHeight;

        const padding = this.padding;

        const occupiedPopupWidth = element.offsetWidth;
        const occupiedPopupHeight = element.offsetHeight;

        const horizontalAnchor = this.horizontalAnchor();
        const verticalAnchor = this.verticalAnchor();
        const popUpOffset = popupMapMarker.icon.getPopUpOffset(this.directionAnchor());
        const horizontalPopupOffset = (horizontalAnchor !== "center") && (verticalAnchor === "center") ? Math.abs(popUpOffset.x) : 0;
        const verticalPopUpOffset = verticalAnchor !== "center" ? Math.abs(popUpOffset.y) : 0;
        const totalOccupiedWidth = occupiedPopupWidth + horizontalPopupOffset + padding;
        const totalOccupiedHeight = occupiedPopupHeight + verticalPopUpOffset + padding;

        if(horizontalAnchor === "left"){
            distanceInPxToRight -= totalOccupiedWidth;
        } else if(horizontalAnchor === "center"){
            distanceInPxToRight -= totalOccupiedWidth / 2;
            distanceInPxToLeft -= totalOccupiedWidth / 2;
        } else {
            distanceInPxToLeft -= totalOccupiedWidth;
        }

        if(verticalAnchor === "top"){
            distanceInPxToBottom -= totalOccupiedHeight;
        } else if(verticalAnchor === "center"){
            distanceInPxToBottom -= totalOccupiedHeight / 2;
            distanceInPxToTop -= totalOccupiedHeight / 2;
        } else {
            distanceInPxToTop -= totalOccupiedHeight;
        }

        return (distanceInPxToLeft >= 0) && (distanceInPxToRight >= 0) && (distanceInPxToTop >= 0) && (distanceInPxToBottom >= 0);
    }

    /**
     *
     * @returns {"bottom"|"top"|"center"}
     */
    verticalAnchor(){
        /** @type {MLD.PositionAnchor[]} */
        const bottomAnchors = ["bottom", "bottom-right", "bottom-left"];
        if(bottomAnchors.includes(this.anchor)){
            return "bottom";
        }
        /** @type {MLD.PositionAnchor[]} */
        const topAnchors = ["top", "top-right", "top-left"];
        if(topAnchors.includes(this.anchor)){
            return "top";
        }
        return "center";
    }

    /**
     *
     * @returns {"right"|"left"|"center"}
     */
    horizontalAnchor(){
        /** @type {MLD.PositionAnchor[]} */
        const rightAnchors = ["right", "bottom-right", "top-right"];
        if(rightAnchors.includes(this.anchor)){
            return "right";
        }
        /** @type {MLD.PositionAnchor[]} */
        const leftAnchors = ["left", "bottom-left", "top-left"];
        if(leftAnchors.includes(this.anchor)){
            return "left";
        }
        return "center";
    }

    /**
     *
     * @returns {"bottom"|"top"|"right"|"left"|"center"}
     */
    directionAnchor() {
        const verticalAnchor = this.verticalAnchor();
        return verticalAnchor !== "center" ? verticalAnchor : this.horizontalAnchor();
    }

    /**
     *
     * @param allowHidden {boolean=}
     * @returns {OSM.MapMarker|null}
     */
    getActivePopupMarker(allowHidden)
    {
        allowHidden = !!allowHidden;
        return !!this.map.selectedMarker && ((!!this.map.selectedMarker.popupContent && this.map.showSelectedMarkerPopup) || allowHidden) ? this.map.selectedMarker : null;
    }

}